import React, { useEffect } from "react"
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { getBookingDetails } from "store/actions/bookingsAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { bookingStatus } from "components/Common/constants"
import { useTranslation } from "react-i18next"
import TableShimmer from "components/Shared/TableShimmer"

const BookingDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Bookings Details `)

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      21,
      "booking_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    }
  }, [userRole])

  const { loading, bookingDetails } = useSelector(state => state.Bookings)

  useEffect(() => {
    const fetchOldDetails = () => {
      const payload = {
        id,
      }
      dispatch(getBookingDetails(payload))
    }
    fetchOldDetails()
  }, [])

  const renderTicketStatus = actionType => {
    return bookingStatus[actionType] || ""
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("Booking Details")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(bookingDetails).length === 0 ? (
                    <p className="text-center my-2">
                      {t("No booking details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Id")}</th>
                          <td>{bookingDetails._id}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Booking Id")}</th>
                          <td>{bookingDetails?.bookingId}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {t(renderTicketStatus(bookingDetails.status))}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name")}</th>
                          <td>{bookingDetails?.userId?.name}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Phone Code")}</th>
                          <td>
                            {bookingDetails?.phoneCode ? (
                              <LtrText>{bookingDetails.phoneCode}</LtrText>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Phone Number")}</th>
                          <td>
                            {bookingDetails?.phoneNumber ? (
                              <LtrText>{bookingDetails.phoneNumber}</LtrText>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Email")}</th>
                          <td style={{ textTransform: "unset" }}>
                            {bookingDetails?.email}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Type")} </th>
                          <td>{t(bookingDetails?.type)}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("City Of Event")}</th>
                          <td>{bookingDetails.cityOfEvent || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Is Electricity Provided")}</th>
                          <td>
                            {bookingDetails.isElectricityProvided
                              ? t("Yes")
                              : t("No")}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Date & Time Of Event")}</th>
                          <td>
                            {formatDateTime(bookingDetails.dateTimeOfEvent) ||
                              "-"}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Number Of People")}</th>
                          <td>{bookingDetails.numberOfPeople ?? "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Venue")}</th>
                          <td>{bookingDetails.venue || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Branch")} </th>
                          <td>
                            <Link
                              to={`https://www.google.com/maps?q=${bookingDetails?.mapLocation?.coordinates[1]},${bookingDetails?.mapLocation?.coordinates[0]}`}
                              target="_blank"
                              className="text-primary"
                            >
                              <i
                                id="location"
                                className="bx bx-pin fs-4 cursor-pointer"
                              ></i>
                            </Link>
                            <UncontrolledTooltip
                              placement="top"
                              target={`location`}
                            >
                              {t("Show In Google Map")}
                            </UncontrolledTooltip>
                          </td>
                        </tr>
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle>{t("Status History")}</CardTitle>
                <div
                  className="table-responsive"
                  style={{ maxHeight: 600, overflow: "auto" }}
                >
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : bookingDetails?.statusHistory?.length === 0 ? (
                    <p className="text-center my-2">
                      {t("No status history found")}
                    </p>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light">
                        <tr>
                          <th>{t("S. No.")}</th>
                          <th>{t("Status")}</th>
                          <th>{t("Remark")}</th>
                          <th>{t("Date")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bookingDetails?.statusHistory?.map(
                          ({ _id, status, description, date }, index) => (
                            <tr key={index}>
                              <th scope="row">{index + 1}</th>
                              <td>{t(renderTicketStatus(status))}</td>
                              <td>{description || "-"}</td>
                              <td>{formatDateTime(date) || "-"}</td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default BookingDetails
